<script setup>

import {defineProps, defineEmits, ref, computed} from "vue";
import LinhaDeParcela from "./LinhaDeParcela.vue";
import UtilNumber from "../../../../utils/UtilNumber";

//==================================================================//
//========================= Props / Emits ==========================//
//==================================================================//

const props = defineProps([
    "revisandoPedido",
    "pedidoDeCompra",
    "somaValorTotal",
    "somaValorTotalFormatado",
    "somaValorSobressalente",
    "somaCustoAdicional",
    "somaParcelasNovasValor",
    "somaValorRestante",
    "parcelasCondicoesDePagamento",
    "controlesDesativados",
]);

const emits = defineEmits([
    "update:adicionaParcela",
    "update:alteraParcela",
    "update:removeParcelaRascunho"
]);

//==================================================================//
//======================= Tabela de Parcelas =======================//
//==================================================================//

const tabelaDeParcelas = ref(null);

//==================================================================//
//======================== Cria Parcela ============================//
//==================================================================//

const adicionaParcela = ()=>{

    emits('update:adicionaParcela');

};

//==================================================================//
//======================== Cria Parcela ============================//
//==================================================================//

const alteraParcela = (update)=>{

    emits('update:alteraParcela',update);

};

//==================================================================//
//================= Remove Parcela Rascunho ========================//
//==================================================================//

const removeParcelaRascunho = (update)=>{

    emits('update:removeParcelaRascunho',update);

};

//==================================================================//
//======================= Parcelas Headers =========================//
//==================================================================//

let parcelasHeaders = [
    { text: 'Status',               value: 'status'},
    { text: 'Remover',              value: 'remover'},
    { text: 'Tipo',                 value: 'tipo'},
    { text: 'Descrição',            value: 'descricao'},
    { text: 'Data Entrega',         value: 'dataEntrega'},
    { text: 'Dias',                 value: 'diasVencimento'},
    { text: 'Data Vencimento',      value: 'dataVencimento'},
    { text: 'Percentual',           value: 'percentual'},
    { text: 'Valor da Parcela',     value: 'valor'},
    { text: 'Referência Evento',    value: 'referenciaEvento'},
]

//==================================================================//
//================ Soma Valor Restante Formatado ===================//
//==================================================================//

let somaValorRestanteFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(props?.somaValorRestante,-999999999,999999999));

});

//==================================================================//
//==================== Soma Valor Restante Cor =====================//
//==================================================================//

const somaValorRestanteCor = computed(()=>{

    if(props?.somaValorRestante == 0){
        return "green";
    }

    return "red";

});

//==================================================================//
//========== Porcentagem Valor Restante do Valor Total =============//
//==================================================================//

let porcentagemValorRestanteDoValorTotal = computed (()=>{

    return (props?.somaValorRestante / props.somaValorTotal) * 100;

});

//==================================================================//
//=================== Porcentagem Valor Total ======================//
//==================================================================//

let porcentagemValorTotal = computed (()=>{

    return 100 - porcentagemValorRestanteDoValorTotal.value;

});

//==================================================================//
//================ Porcentagem Valor Total Formatado ===============//
//==================================================================//

let porcentagemValorTotalFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(porcentagemValorTotal.value,-999,999));

});

//==================================================================//
//=================== Porcentagem Valor Total Cor ==================//
//==================================================================//

const porcentagemValorTotalCor = computed(()=>{

    return porcentagemValorTotal.value != 100 ? "red" : "green";

});

//==================================================================//
//====== Porcentagem Valor Restante do Valor das Parcelas ==========//
//==================================================================//

let porcentagemValorRestanteDoValorDasParcelas = computed (()=>{

    if(props.somaCustoAdicional == 0){
        return 0;
    }

    return (props?.somaParcelasNovasValor / props.somaCustoAdicional) * 100;

});

//==================================================================//
//================= Porcentagem Valor Restante =====================//
//==================================================================//

let porcentagemValorRestante = computed (()=>{

    if(props?.somaCustoAdicional == 0){
        return 0;
    }

    return 100 - porcentagemValorRestanteDoValorDasParcelas.value;

});

//==================================================================//
//================ Porcentagem Valor Total Formatado ===============//
//==================================================================//

let porcentagemValorRestanteFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(porcentagemValorRestante.value,-999,999));

});

//==================================================================//
//================ Parcela Percentual Restante Cor =================//
//==================================================================//

const porcentagemValorRestanteCor = computed(()=>{

    return porcentagemValorRestante.value != 0 ? "red" : "green";

});

//==================================================================//
//======================== Adiciona Parcela ========================//
//==================================================================//

const aoAdicinarParcela = ()=>{
    adicionaParcela();
};

//==================================================================//
//======================= Desativa Nova Parcela ====================//
//==================================================================//

const desativaNovaParcela = computed(()=>{

    if(props?.somaValorRestante == 0){
        return true;
    }

    if(props.controlesDesativados){
        return true;
    }

    return false;

});

</script>

<template>

    <v-card
        ref="tabelaDeParcelas"
    >

        <v-card-title class="c-parcelas">

            <h3
                class="c-parcelas__titulo"
            >
                Parcelas do Pedido
            </h3>

            <div
                class="c-parcelas__medidores"
            >

                <div
                    class="c-medidor"
                >

                    <v-chip
                        class="c-medidor__porcentagem-total"
                        :color="porcentagemValorTotalCor"
                        dark
                        label
                    >

                        Porcentagem Total:
                        {{porcentagemValorTotalFormatado}}

                        <v-icon right>
                            mdi-percent
                        </v-icon>

                    </v-chip>

                    
                    <v-chip
                        class="c-medidor__porcentagem-restante"
                        :color="porcentagemValorRestanteCor"
                        dark
                        label
                    >

                        Porcentagem Restante:
                        {{porcentagemValorRestanteFormatado}}

                        <v-icon right>
                            mdi-percent
                        </v-icon>

                    </v-chip>

                </div>

                <div
                    class="c-medidor"
                >

                    <v-chip
                        class="c-medidor__porcentagem-total"
                        color="green"
                        dark
                        label
                    >

                        Valor Total:
                        R$
                        {{props?.somaValorTotalFormatado}}

                    </v-chip>

                    <v-chip
                        class="c-medidor__porcentagem-restante"
                        :color="somaValorRestanteCor"
                        dark
                        label
                    >

                        Valor Restante:
                        R$
                        {{somaValorRestanteFormatado}}

                    </v-chip>

                </div>

            </div>

            <v-btn
                class="c-parcelas__nova-parcela"
                color="primary"
                dark
                :disabled="desativaNovaParcela"
                @click="aoAdicinarParcela"
            >
                Nova Parcela
            </v-btn>

        </v-card-title>

        <div class="c-table-wrapper">

            <div class="c-table">

                <div class="c-table__headers">

                    <div class="c-table__header" v-for="parcelaHeader in parcelasHeaders"
                        :key="parcelaHeader.value"
                    >

                        <div class="c-table__header-content">{{parcelaHeader.text}}</div>
                        
                    </div>

                </div>
                <div class="c-table__body">

                    <LinhaDeParcela v-for="parcelaLinha in props?.pedidoDeCompra?.parcelas"
                        :key="parcelaLinha.id"
                        :pedidoDeCompra="props?.pedidoDeCompra"
                        :somaCustoAdicional="props?.somaCustoAdicional"
                        :parcela="parcelaLinha"
                        :parcelasCondicoesDePagamento="props.parcelasCondicoesDePagamento"
                        :controlesDesativados="props.controlesDesativados"
                        @update:alteraParcela="alteraParcela"
                        @update:removeParcelaRascunho="removeParcelaRascunho"
                    ></LinhaDeParcela>

                </div>

            </div>

        </div>

    </v-card>

</template>

<style scoped>

.c-parcelas{
    display: grid; 
    grid-template-columns: 1fr 1fr; 
    grid-template-rows: 1fr min-content; 
    gap: 10px; 
    grid-template-areas: 
        "titulo ."
        "medidores novaParcela";
}

.c-parcelas__titulo {
    grid-area: titulo;
}

.c-parcelas__medidores{
    grid-area: medidores;
    display: grid;
    grid-auto-flow: row;
    grid-gap: 10px;
}

.c-medidor{
    display: flex;
    grid-gap: 10px;
    flex-wrap: wrap;
}

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg.c-parcelas__nova-parcela{
    background: #1976d2 !important;
    border-color: #1976d2 !important;
}

.c-parcelas__nova-parcela { 
    grid-area: novaParcela;
    align-self: baseline;
    width: min-content;
    margin-left: auto;
}

.c-table-wrapper{
    overflow: hidden !important;
    width: calc(100vw - 24px);
    padding: 12px !important;
}

.c-table{
    display: grid;
    grid-template-columns: 110px 90px 180px minmax(390px, auto) 150px 150px 150px 150px 150px 210px;
    overflow: auto;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
}

.c-table__headers{
    display: contents;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.75rem;
}

.c-table__header{
    padding: 14px 16px;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
    font-weight: 700;
}

.c-table__body{
    display: contents;
}

.c-table__row{
    display: contents;
}

.c-table__row:hover > .c-table__cell{
    background-color: #eeeeee;
}

.c-table__cell{
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 13px 16px;
    font-size: 0.875rem;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
</style>